import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Field, FieldType, MemberAddeditComponent, MemberAddeditDialogModel } from '../../brokeragency/member-addedit/member-addedit.component';
import { FinanceLedgerService } from '../../../services/finance-ledger/finance-ledger.service';
import { Contract, CreateFundsMovementRequest, FundsMovement, ReclassifyFundsMovementRequest } from '../../../services/ledger-client/types';
import { FINANCE_TABS, FinanceTab } from '../finance-tabs';

interface FundsMovementDialogPayload {
  contractId: string;
  provider: 'STRIPE' | 'DWOLLA' | 'MANUAL';
  movementType: string;
  direction: 'IN' | 'OUT';
  grossAmount: number | string;
  feeAmount?: number | string;
  netAmount?: number | string;
  externalId?: string;
  externalStatus?: string;
  occurredAt?: string | Date;
  metadata?: Record<string, unknown>;
  idempotencyKey?: string;
}

interface ReclassifyDialogPayload {
  contractId: string;
  postToLedger?: boolean;
  metadataPatch?: Record<string, unknown>;
}

@Component({
    selector: 'app-finance-funds-movements',
    templateUrl: './finance-funds-movements.component.html',
    styleUrls: ['./finance-funds-movements.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FinanceFundsMovementsComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  tabs: FinanceTab[] = FINANCE_TABS;
  activeTab: FinanceTab = this.tabs.find((tab) => tab.id === 'funds-movements') || this.tabs[0];
  allColumnNames = ['createdAt', 'contractId', 'provider', 'movementType', 'direction', 'grossAmount', 'status', 'ledgerTransactionId', 'actionmenu'];
  unmappedColumnNames = ['provider', 'movementType', 'direction', 'grossAmount', 'occurredAt', 'status', 'actionmenu'];
  items = new MatTableDataSource<FundsMovement>();
  unmappedItems = new MatTableDataSource<FundsMovement>();
  movementSearchFilter = '';
  movementsLoading = false;
  movementsLoaded = 0;
  hasMoreMovements = true;
  contracts: Contract[] = [];
  unmappedProviderFilter: 'ALL' | 'STRIPE' | 'DWOLLA' | 'MANUAL' = 'ALL';
  unmappedSearchFilter = '';
  unmappedFromDate = '';
  unmappedToDate = '';
  private unmappedAllItems: FundsMovement[] = [];
  private allItems: FundsMovement[] = [];
  private movementLoadGeneration = 0;
  private movementOffset = 0;
  private readonly movementPageSize = 200;
  private readonly movementSearch$ = new Subject<string>();

  private readonly destroy$ = new Subject<void>();
  private readonly addFields: Array<Field> = [
    { name: 'contractId', label: 'Contract ID', fieldType: FieldType.F_STRING, required: true },
    { name: 'provider', label: 'Provider', fieldType: FieldType.F_DROPDOWN, values: ['STRIPE', 'DWOLLA', 'MANUAL'], required: true },
    { name: 'movementType', label: 'Movement Type', fieldType: FieldType.F_STRING, required: true },
    { name: 'direction', label: 'Direction', fieldType: FieldType.F_DROPDOWN, values: ['IN', 'OUT'], required: true },
    { name: 'grossAmount', label: 'Gross Amount', fieldType: FieldType.F_NUMBER, required: true },
    { name: 'feeAmount', label: 'Fee Amount', fieldType: FieldType.F_NUMBER },
    { name: 'netAmount', label: 'Net Amount (optional)', fieldType: FieldType.F_NUMBER },
    { name: 'externalId', label: 'External ID', fieldType: FieldType.F_STRING },
    { name: 'externalStatus', label: 'External Status', fieldType: FieldType.F_STRING },
    { name: 'occurredAt', label: 'Occurred At', fieldType: FieldType.F_ANYDATE },
    { name: 'idempotencyKey', label: 'Idempotency Key', fieldType: FieldType.F_STRING, required: true },
    { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON },
  ];
  private readonly reclassifyFields: Array<Field> = [
    { name: 'contractId', label: 'Target Contract', fieldType: FieldType.F_DROPDOWN, values: [], required: true },
    { name: 'postToLedger', label: 'Post To Ledger', fieldType: FieldType.F_TOGGLE },
    { name: 'metadataPatch', label: 'Metadata Patch JSON', fieldType: FieldType.F_JSON },
  ];
  private readonly detailsFields: Array<Field> = [
    { name: 'id', label: 'ID', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'contractId', label: 'Contract ID', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'provider', label: 'Provider', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'movementType', label: 'Movement Type', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'direction', label: 'Direction', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'currency', label: 'Currency', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'grossAmount', label: 'Gross Amount', fieldType: FieldType.F_NUMBER, readOnly: true },
    { name: 'feeAmount', label: 'Fee Amount', fieldType: FieldType.F_NUMBER, readOnly: true },
    { name: 'netAmount', label: 'Net Amount', fieldType: FieldType.F_NUMBER, readOnly: true },
    { name: 'externalId', label: 'External ID', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'externalStatus', label: 'External Status', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'status', label: 'Status', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'occurredAt', label: 'Occurred At', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'createdAt', label: 'Created At', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'ledgerTransactionId', label: 'Ledger Tx ID', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'settlementBatchId', label: 'Settlement Batch ID', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON, readOnly: true },
  ];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private financeLedgerService: FinanceLedgerService,
  ) {}

  ngOnInit(): void {
    this.setActiveTab();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => this.setActiveTab());
    this.movementSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => this.reloadItems());
    this.reloadAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setActiveTab(): void {
    const parsedUrl = this.router.parseUrl(this.router.url);
    const url = parsedUrl.toString().split('?')[0];
    this.activeTab = this.tabs.find((tab) => tab.link === url) || this.tabs[0];
  }

  gotoTab(tab: FinanceTab): void {
    if (this.activeTab?.id === tab.id) {
      return;
    }
    this.router.navigate([tab.link]).then();
    this.activeTab = tab;
  }

  addItem(): void {
    const defaultPayload = {
      provider: 'MANUAL',
      direction: 'IN',
      idempotencyKey: this.financeLedgerService.generateIdempotencyKey(),
      metadata: {},
    };

    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(defaultPayload, this.addFields, 'Funds Movement')
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      const payload = this.getValidPayload(dialogResult);
      if (!payload) {
        return;
      }

      this.financeLedgerService.ingestFundsMovement(payload).then(() => {
        this.reloadAll();
        this.snackBar.open('Funds movement ingested and posting attempted', 'Close', { duration: 3000 });
      }).catch((error: unknown) => {
        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to ingest funds movement'), 'Close', { duration: 3000 });
      });
    });
  }

  reclassifyUnmapped(item: FundsMovement): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel({
        contractId: '',
        postToLedger: true,
        metadataPatch: {},
      }, this.getReclassifyFields(), 'Unmapped Funds Movement', true, 'Edit'),
    });

    dialogRef.afterClosed().subscribe((dialogResult: ReclassifyDialogPayload | null) => {
      const payload = this.getValidReclassifyPayload(dialogResult);
      if (!payload) {
        return;
      }

      this.financeLedgerService.reclassifyAndPostFundsMovement(item.id, payload).then(() => {
        this.reloadAll();
        this.snackBar.open('Movement reclassified', 'Close', { duration: 3000 });
      }).catch((error: unknown) => {
        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to reclassify movement'), 'Close', { duration: 3000 });
      });
    });
  }

  openFundsMovementDetails(item: FundsMovement): void {
    this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel({
        ...item,
        createdAt: this.getCreatedAt(item),
      }, this.detailsFields, 'Funds Movement', true, 'View'),
    });
  }

  getCreatedAt(item: FundsMovement): string {
    return String((item as any)?.createdAt || item.occurredAt || '');
  }

  private getValidPayload(payload: FundsMovementDialogPayload | null): (CreateFundsMovementRequest & { idempotencyKey: string }) | null {
    if (!payload) {
      return null;
    }

    if (!payload.contractId || !String(payload.contractId).trim()) {
      this.snackBar.open('contractId is required', 'Close', { duration: 3000 });
      return null;
    }

    if (!payload.movementType || !String(payload.movementType).trim()) {
      this.snackBar.open('movementType is required', 'Close', { duration: 3000 });
      return null;
    }

    const grossAmount = Number(payload.grossAmount);
    if (!Number.isFinite(grossAmount)) {
      this.snackBar.open('grossAmount must be a number', 'Close', { duration: 3000 });
      return null;
    }

    const feeAmount = payload.feeAmount !== undefined && payload.feeAmount !== null && payload.feeAmount !== ''
      ? Number(payload.feeAmount)
      : undefined;

    if (feeAmount !== undefined && !Number.isFinite(feeAmount)) {
      this.snackBar.open('feeAmount must be a number when provided', 'Close', { duration: 3000 });
      return null;
    }

    const netAmount = payload.netAmount !== undefined && payload.netAmount !== null && payload.netAmount !== ''
      ? Number(payload.netAmount)
      : undefined;

    if (netAmount !== undefined && !Number.isFinite(netAmount)) {
      this.snackBar.open('netAmount must be a number when provided', 'Close', { duration: 3000 });
      return null;
    }

    const idempotencyKey = String(payload.idempotencyKey || '').trim();
    if (!idempotencyKey) {
      this.snackBar.open('idempotencyKey is required', 'Close', { duration: 3000 });
      return null;
    }

    const occurredAt = this.normalizeDate(payload.occurredAt);
    if (payload.occurredAt && !occurredAt) {
      this.snackBar.open('occurredAt must be a valid date', 'Close', { duration: 3000 });
      return null;
    }

    const metadata = payload.metadata && typeof payload.metadata === 'object' && !Array.isArray(payload.metadata)
      ? payload.metadata
      : {};

    return {
      contractId: String(payload.contractId).trim(),
      provider: payload.provider,
      movementType: String(payload.movementType).trim(),
      direction: payload.direction,
      grossAmount,
      feeAmount,
      netAmount,
      externalId: payload.externalId ? String(payload.externalId).trim() : undefined,
      externalStatus: payload.externalStatus ? String(payload.externalStatus).trim() : undefined,
      occurredAt: occurredAt || undefined,
      metadata,
      idempotencyKey,
    };
  }

  private normalizeDate(value: string | Date | undefined): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }

  reloadItems(): void {
    const generation = ++this.movementLoadGeneration;
    this.allItems = [];
    this.items.data = [];
    this.movementsLoaded = 0;
    this.movementOffset = 0;
    this.hasMoreMovements = true;
    this.loadMovementPage(generation);
  }

  onMovementSearchChange(): void {
    this.movementSearch$.next(this.movementSearchFilter.trim());
  }

  clearMovementFilter(): void {
    this.movementSearchFilter = '';
    this.movementSearch$.next('');
  }

  onMovementScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const remainingScroll = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remainingScroll > 120 || this.movementsLoading || !this.hasMoreMovements) {
      return;
    }

    this.loadMovementPage(this.movementLoadGeneration);
  }

  reloadAll(): void {
    this.reloadItems();
    this.reloadUnmappedItems();
    this.reloadContracts();
  }

  private loadMovementPage(generation: number): void {
    this.movementsLoading = true;
    this.financeLedgerService.listFundsMovements(
      this.movementPageSize,
      this.movementOffset,
      this.movementSearchFilter.trim(),
    ).then((movements) => {
      if (generation !== this.movementLoadGeneration) {
        return;
      }

      const existingIds = new Set(this.allItems.map((item) => item.id));
      const newMovements = movements.filter((item) => !existingIds.has(item.id));
      this.allItems = this.allItems.concat(newMovements)
        .sort((movement1, movement2) => movement2.occurredAt.localeCompare(movement1.occurredAt));
      this.movementsLoaded = this.allItems.length;
      this.items.data = this.allItems;
      this.items.sort = this.sort;
      this.movementOffset += movements.length;
      this.hasMoreMovements = movements.length === this.movementPageSize;
      this.movementsLoading = false;

      if (movements.length > 0 && newMovements.length === 0) {
        this.hasMoreMovements = false;
        this.snackBar.open('Unable to load additional funds movements because the result page was repeated', 'Close', { duration: 5000 });
      }
    }).catch((error: unknown) => {
      if (generation !== this.movementLoadGeneration) {
        return;
      }
      this.movementsLoading = false;
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load funds movements'), 'Close', { duration: 3000 });
    });
  }

  reloadUnmappedItems(): void {
    this.financeLedgerService.listUnmappedFundsMovements().then((movements) => {
      this.unmappedAllItems = movements.sort((movement1, movement2) => movement2.occurredAt.localeCompare(movement1.occurredAt));
      this.applyUnmappedFilters();
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load unmapped movements'), 'Close', { duration: 3000 });
    });
  }

  reloadContracts(): void {
    this.financeLedgerService.listContracts().then((contracts) => {
      this.contracts = contracts.sort((contract1, contract2) => contract1.name.localeCompare(contract2.name));
      this.reclassifyFields[0].values = this.contracts.map((contract) => `${contract.name}:${contract.id}`);
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load contracts for reclassification'), 'Close', { duration: 3000 });
    });
  }

  getClassificationState(item: FundsMovement): string {
    const classification = (item.metadata as any)?.classification;
    return String(classification?.state || '').trim();
  }

  applyUnmappedFilters(): void {
    const searchTerm = this.unmappedSearchFilter.trim().toLowerCase();
    const fromTimestamp = this.toDayStart(this.unmappedFromDate);
    const toTimestamp = this.toDayEnd(this.unmappedToDate);

    this.unmappedItems.data = this.unmappedAllItems.filter((item) => {
      if (this.unmappedProviderFilter !== 'ALL' && item.provider !== this.unmappedProviderFilter) {
        return false;
      }

      const occurredAtTimestamp = new Date(item.occurredAt).getTime();
      if (fromTimestamp !== null && occurredAtTimestamp < fromTimestamp) {
        return false;
      }
      if (toTimestamp !== null && occurredAtTimestamp > toTimestamp) {
        return false;
      }

      if (!searchTerm) {
        return true;
      }

      const searchable = [
        item.id,
        item.provider,
        item.movementType,
        item.direction,
        item.externalId || '',
        item.externalStatus || '',
        this.getClassificationState(item),
      ].join(' ').toLowerCase();

      return searchable.includes(searchTerm);
    });
  }

  clearUnmappedFilters(): void {
    this.unmappedProviderFilter = 'ALL';
    this.unmappedSearchFilter = '';
    this.unmappedFromDate = '';
    this.unmappedToDate = '';
    this.applyUnmappedFilters();
  }

  private toDayStart(value: string): number | null {
    if (!value) {
      return null;
    }
    return new Date(`${value}T00:00:00.000Z`).getTime();
  }

  private toDayEnd(value: string): number | null {
    if (!value) {
      return null;
    }
    return new Date(`${value}T23:59:59.999Z`).getTime();
  }

  private getReclassifyFields(): Array<Field> {
    return this.reclassifyFields.map((field) => ({ ...field }));
  }

  private getValidReclassifyPayload(payload: ReclassifyDialogPayload | null): ReclassifyFundsMovementRequest | null {
    if (!payload) {
      return null;
    }

    const contractId = String(payload.contractId || '').trim();
    if (!contractId) {
      this.snackBar.open('contractId is required', 'Close', { duration: 3000 });
      return null;
    }

    if (payload.metadataPatch !== undefined && (typeof payload.metadataPatch !== 'object' || payload.metadataPatch === null || Array.isArray(payload.metadataPatch))) {
      this.snackBar.open('metadataPatch must be a JSON object', 'Close', { duration: 3000 });
      return null;
    }

    return {
      contractId,
      postToLedger: payload.postToLedger !== false,
      metadataPatch: payload.metadataPatch || {},
    };
  }
}
